'use client'

import { Fragment } from 'react'
import { Dialog, DialogPanel, Transition, TransitionChild } from '@headlessui/react'
import { PackageX, Plus, X } from 'lucide-react'
import { formatImageURL, formatPrice } from '@/utils/util'
import { useRewardVariantPicker, useVariantSelection } from '@/lib/commerce/hooks'
import VariantOptionPicker from '@/components/blocks/VariantOptionPicker'

function RewardPrice({ price, original }: { price: number, original?: number }) {
  return (
    <div className="flex items-baseline gap-2 flex-wrap">
      <span className="text-[13px] font-semibold tabular-nums" style={{ color: price === 0 ? '#34d399' : '#fda4af' }}>{price === 0 ? 'Үнэгүй' : formatPrice(price)}</span>
      {!!original && price < original && <span className="text-[11px] tabular-nums line-through" style={{ color: 'rgba(255,255,255,0.4)' }}>{formatPrice(original)}</span>}
    </div>
  )
}

function RewardOptionChoice({ product, getPrice, onAdd }: { product: any, getPrice: (_variant: any) => number, onAdd: (_variantId: string) => void }) {
  const { selectedVariant, selectedOptions, selectOption, isSelectionPurchasable, isVariantAvailable } = useVariantSelection(product)
  const canAdd = isSelectionPurchasable && !!selectedVariant?.id && isVariantAvailable(selectedVariant)

  return (
    <div className="space-y-5">
      <div className="flex items-center gap-3">
        <div className="w-16 h-16 shrink-0 rounded-md overflow-hidden" style={{ background: 'rgba(255,255,255,0.035)', border: '1px solid rgba(255,255,255,0.07)' }}>
          <img src={formatImageURL(selectedVariant?.images?.[0] || product.image || 'placeholder.png')} alt={product.name} className="w-full h-full object-cover" />
        </div>
        <div className="min-w-0">
          <div className="text-[13px] truncate" style={{ color: 'rgba(255,255,255,0.6)' }}>{isSelectionPurchasable ? selectedVariant?.name : 'Энэ хослол байхгүй'}</div>
          {selectedVariant && <div className="mt-1"><RewardPrice price={getPrice(selectedVariant)} original={selectedVariant.price} /></div>}
        </div>
      </div>
      <VariantOptionPicker product={product} selected={selectedOptions} onSelect={selectOption} tone="dark" />
      <button type="button" disabled={!canAdd} onClick={() => { if (selectedVariant?.id) onAdd(selectedVariant.id) }} className="w-full h-11 rounded-full text-[13px] font-semibold transition-opacity cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed" style={{ background: 'white', color: '#0a0a0c' }}>
        {canAdd ? 'Сагсанд нэмэх' : 'Дууссан байна'}
      </button>
    </div>
  )
}

export default function RewardVariantModal() {
  const { isOpen, close, reward, variants, optionProduct, add, getPrice, isAvailable } = useRewardVariantPicker()

  return (
    <Transition show={isOpen} as={Fragment}>
      <Dialog as="div" className="relative z-60" onClose={close}>
        <TransitionChild as={Fragment} enter="ease-out duration-300" enterFrom="opacity-0" enterTo="opacity-100" leave="ease-in duration-200" leaveFrom="opacity-100" leaveTo="opacity-0">
          <div className="fixed inset-0 bg-black/70 backdrop-blur-[2px]" />
        </TransitionChild>

        <div className="fixed inset-0 z-10 overflow-y-auto">
          <div className="flex min-h-full items-end sm:items-center justify-center p-0 sm:p-4">
            <TransitionChild as={Fragment} enter="transition ease-out duration-300 transform" enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-[0.985]" enterTo="opacity-100 translate-y-0 sm:scale-100" leave="transition ease-in duration-200 transform" leaveFrom="opacity-100 translate-y-0 sm:scale-100" leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-[0.985]">
              <DialogPanel className="w-full sm:max-w-lg max-h-[85vh] sm:max-h-[80vh] flex flex-col overflow-hidden rounded-t-2xl sm:rounded-3xl" style={{ background: 'linear-gradient(180deg, #16161c 0%, #0c0c10 100%)', border: '1px solid rgba(255,255,255,0.13)', boxShadow: '0 40px 100px -20px rgba(0,0,0,0.75)' }}>
                <div className="px-6 pt-6 pb-5 shrink-0" style={{ borderBottom: '1px solid rgba(255,255,255,0.07)' }}>
                  <div className="flex items-start justify-between gap-4">
                    <div className="min-w-0 flex-1">
                      <div className="text-[10px] font-semibold uppercase tracking-[0.22em]" style={{ color: '#fb923c' }}>Бэлгийн барааны төрөл сонгох</div>
                      <h3 className="mt-1 text-[16px] font-semibold leading-tight text-white truncate" style={{ fontFamily: 'Gilroy, Inter, sans-serif' }}>{reward?.name}</h3>
                      <p className="mt-1 text-[12px] leading-snug" style={{ color: 'rgba(255,255,255,0.55)' }}>Та хүссэн төрлөө сонгоод сагсанд нэмнэ үү</p>
                    </div>
                    <button type="button" onClick={close} className="h-8 w-8 grid place-items-center rounded-full transition-colors cursor-pointer shrink-0" style={{ color: 'rgba(255,255,255,0.5)' }} aria-label="Хаах">
                      <X className="h-4 w-4" />
                    </button>
                  </div>
                </div>

                <div className="px-6 py-5 overflow-y-auto scrollbar-dark space-y-2">
                  {optionProduct ? (
                    <RewardOptionChoice product={optionProduct} getPrice={getPrice} onAdd={add} />
                  ) : variants.map((variant: any) => {
                    const available = isAvailable(variant)
                    return (
                      <button key={variant.id} type="button" disabled={!available} onClick={() => add(variant.id)} className="w-full text-left rounded-xl p-3 transition-colors cursor-pointer disabled:cursor-not-allowed" style={{ background: 'rgba(0,0,0,0.3)', border: '1px solid rgba(255,255,255,0.1)', opacity: available ? 1 : 0.5 }}>
                        <div className="flex items-center gap-3">
                          <div className="w-14 h-14 shrink-0 rounded-md overflow-hidden" style={{ background: 'rgba(255,255,255,0.035)', border: '1px solid rgba(255,255,255,0.07)' }}>
                            <img src={formatImageURL(variant.images?.[0] || reward?.image || 'placeholder.png')} alt={variant.name} className="w-full h-full object-cover" />
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="text-[13.5px] font-medium text-white truncate">{variant.name}</div>
                            <div className="mt-1"><RewardPrice price={getPrice(variant)} original={variant.price} /></div>
                            {!available && <div className="mt-1 text-[11px]" style={{ color: '#fda4af' }}>Дууссан</div>}
                          </div>
                          <div className="shrink-0">
                            {available ? (
                              <div className="h-9 w-9 rounded-full grid place-items-center" style={{ background: 'white', color: '#0a0a0c' }}>
                                <Plus className="h-4 w-4" />
                              </div>
                            ) : (
                              <div className="h-9 w-9 rounded-full grid place-items-center" style={{ background: 'rgba(244,63,94,0.16)', color: '#fda4af', border: '1px solid rgba(244,63,94,0.3)' }}>
                                <PackageX className="h-4 w-4" />
                              </div>
                            )}
                          </div>
                        </div>
                      </button>
                    )
                  })}
                </div>

                <div className="px-6 py-4 shrink-0" style={{ borderTop: '1px solid rgba(255,255,255,0.07)', background: 'rgba(0,0,0,0.3)' }}>
                  <button type="button" onClick={close} className="w-full h-11 rounded-full text-[13px] font-medium cursor-pointer transition-colors" style={{ background: 'transparent', color: 'white', border: '1px solid rgba(255,255,255,0.2)' }}>Цуцлах</button>
                </div>
              </DialogPanel>
            </TransitionChild>
          </div>
        </div>
      </Dialog>
    </Transition>
  )
}
